import {
  areAnyStringsLongerThan,
  calculateLongestLabelWidth,
  getTextLines,
} from "./graphUtils";
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Label,
  LabelList,
  Text,
} from "recharts";
import { useIsMobileWidth, useWindowDimensions } from "@src/utils/mediaQuery";
import AppUtils from "@src/utils/appUtils";
import PropTypes from "prop-types";

const calculateTextWidth = (textString) => {
  const charWidth = 7;
  const rectPadding = 5;
  return Array.isArray(textString)
    ? Math.max(...textString.map((text) => text.length * charWidth)) +
        rectPadding * 2
    : textString.length * charWidth + rectPadding * 2;
};

const CustomizedLabel = (props) => {
  const {
    y,
    x,
    width,
    height,
    value,
    formatter,
    mobileLayout,
    invertBarLabels,
    isExample = false,
  } = props;

  if (isExample) {
    return null;
  }
  let xPos = mobileLayout ? x + width / 2 : (width + x) * 1.01;

  const newValue = formatter(value);
  let line1 = "";
  let line2 = "";
  let labelHeight = mobileLayout ? 20 : height;

  // Calculation for rect width
  let calculatedWidth = calculateTextWidth(newValue);

  if (invertBarLabels && !mobileLayout) {
    xPos = x + width - (calculatedWidth - 2);
  } else if (invertBarLabels && mobileLayout) {
    xPos = x + width / 2 - 20;
  }

  let whiteBackgroundYPos = mobileLayout ? y - 20 : y;
  let whiteBackgroundXPos = mobileLayout ? xPos - calculatedWidth / 2 : xPos;

  // recalculate for 2 lines if text is too long and layout is mobile
  if (calculatedWidth > 120 && mobileLayout) {
    // work out how to split lines
    const words = newValue.split(" ");
    const numOfWordsOnFirstLine = Math.ceil(words.length / 2);
    const wordsForFirstLine = words.slice(0, numOfWordsOnFirstLine);
    const wordsForSecondLine = words.slice(numOfWordsOnFirstLine, words.length);
    // create new lines
    line1 = wordsForFirstLine.join(" ");
    line2 = wordsForSecondLine.join(" ");
    // work out new width and position for label
    const line1TextWidth = calculateTextWidth(line1);
    const line2TextWidth = calculateTextWidth(line2);
    calculatedWidth =
      line1TextWidth > line2TextWidth ? line1TextWidth : line2TextWidth;
    labelHeight = 40;
    whiteBackgroundYPos = invertBarLabels
      ? y + labelHeight / 2
      : y - labelHeight - 5;
    whiteBackgroundXPos = invertBarLabels
      ? xPos - calculatedWidth / 3
      : xPos - calculatedWidth / 2;
  } else {
    line1 = newValue;
  }

  return (
    <g>
      <foreignObject
        x={whiteBackgroundXPos}
        y={whiteBackgroundYPos}
        width={calculatedWidth}
        height={labelHeight}
      >
        <div className="bg-canvas w-full h-full flex flex-col items-center justify-center text-nowrap text-sm">
          <div>{line1}</div>
          {line2 && <div>{line2}</div>}
        </div>
      </foreignObject>
    </g>
  );
};
const stackedCustomizedLabel = (props) => {
  let { x, y, width, value, formatter, screenWidth } = props;
  const radius = 20;
  x = x ? x : 0;
  y = y ? y : 0;

  const newValue = formatter(value);
  const gap = screenWidth < 400 ? 9 : 18;

  // Calculation for rect width
  const charWidth = 7;
  const rectPadding = 5;
  const calculatedWidth = Array.isArray(newValue)
    ? Math.max(...newValue.map((text) => text.length * charWidth)) +
      rectPadding * 2
    : newValue.length * charWidth + rectPadding * 2;

  const box = Math.max(width + gap * 2, calculatedWidth); // width + gap * 2;
  const offset = gap;
  const xPlace = x - offset;

  return (
    <g>
      <foreignObject
        x={xPlace}
        y={y - radius * 2}
        width={box}
        height={
          Array.isArray(newValue) ? radius * newValue.length - 2 : radius * 1.5
        }
      >
        <div className="bg-canvas w-full h-full flex items-center justify-center text-sm">
          {newValue}
        </div>
      </foreignObject>
    </g>
  );
};

const CustomizedRefLabel = (payload) => {
  const { value, viewBox, fill } = payload;
  const { x, y, width } = viewBox;
  const lineCount = value ? getTextLines(value, 200) : 2;

  return (
    <g key={`key-${x}-${y}`} transform={`translate(${x},${y})`} width={90}>
      <Text
        x={width}
        y={lineCount * 5}
        width={90}
        fontSize={"0.875rem !important"}
        textAnchor="right"
        fill={fill}
      >
        {value}
      </Text>
    </g>
  );
};

const CustomizedAxisTick = (props) => {
  const { x, y, payload } = props;

  const lineCount = getTextLines(payload.value, 75);
  return (
    <g key={`key-${x}-${y}`} transform={`translate(${x},${y})`} width={75}>
      <Text
        x={0}
        y={0 + lineCount * 10}
        dy={16}
        width={70}
        fontSize={"0.875rem !important"}
        textAnchor="middle"
        fill="black"
      >
        {payload.value}
      </Text>
    </g>
  );
};

const CustomizedMobileAxisTick = (props) => {
  const { x, y, payload } = props;

  return (
    <g key={`key-${x}-${y}`} transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        fontSize={"0.875rem !important"}
        textAnchor="end"
        fill="black"
        transform="rotate(-35)"
      >
        {payload.value}
      </text>
    </g>
  );
};

const BehaviourBarGraph = (props) => {
  let {
    data,
    threshold,
    additionalThresholdsRelative,
    additionalThresholdsActualValues,
    thresholdLabel,
    moveOrigin = false,
    originLabel,
    domain,
    mobileDomain,
    refLineColour,
    success,
    unitOfMeasure = "kg",
    axisLabel,
    barLabelFormatter,
    barLabelFormatterMobile,
    invertBarLabels,
    showTicks = true,
    hideNum = false,
    hideYLine = false,
    ticks = undefined,
    hideTopLabel = false,
    hideYAxisOnDesktop = false,
    hideYLabelOnMobile = false,
    hideXLineOnMobile = false,
    isExample = false,
  } = props;

  if (!barLabelFormatter) {
    barLabelFormatter = (value) =>
      `${Math.floor(value)}${unitOfMeasure !== "%" ? " " : ""}${unitOfMeasure}`;
  }

  const belowThresholdAnimationDuration = 500;
  const mobileLayout = useIsMobileWidth();
  const { width } = useWindowDimensions();
  const margins = {
    top: 50,
    right: mobileLayout ? 90 : 5,
    bottom: 50,
    left: 30,
  };
  const barSize = 40;

  const textArray = [];
  const mobileLabels = [];

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (item.name) textArray.push(item.name); // Collect names
    if (item.mobileName) mobileLabels.push(item.mobileName); // Collect mobile names
  }

  const needsLegend = areAnyStringsLongerThan(textArray, 100);
  const maxHeight =
    mobileLabels.length > 0 ? calculateLongestLabelWidth(mobileLabels) : 100;

  const additionalThresholds = additionalThresholdsRelative
    ? additionalThresholdsRelative
    : additionalThresholdsActualValues;
  return (
    <div id={"signol-chart-container"}>
      {mobileLayout ? (
        <>
          <ResponsiveContainer height={width * 1.4} width={"100%"}>
            <BarChart
              with={"100%"}
              data={data}
              barSize={40}
              barCategoryGap={40}
              margin={margins}
            >
              <YAxis
                domain={mobileDomain ? mobileDomain : domain}
                axisLine={!hideYLine}
                tickLine={!hideYLine}
                tick={{
                  fill: hideNum ? "none" : "black",
                  fontSize: "0.875rem",
                }}
                ticks={ticks?.length > 0 ? ticks : undefined}
              >
                {!hideYLabelOnMobile && (
                  <Label
                    value={axisLabel}
                    angle="-90"
                    position="insidemiddle"
                    fill="black"
                  />
                )}
              </YAxis>
              <XAxis
                type="category"
                dataKey={!!needsLegend ? "mobileName" : "name"}
                tick={
                  width < 480 ? (
                    <CustomizedMobileAxisTick />
                  ) : (
                    <CustomizedAxisTick />
                  )
                }
                tickLine={false}
                minTickGap={1}
                height={maxHeight}
                axisLine={!hideXLineOnMobile}
              />
              {!!moveOrigin && (
                <ReferenceLine
                  key={`ref-line-origin`}
                  label={{
                    position: "right",
                    value: originLabel,
                    width: 75,
                    fill: refLineColour,
                  }}
                  y={0}
                  stroke={refLineColour}
                  strokeWidth="3"
                />
              )}
              {additionalThresholds &&
                additionalThresholds.map((listThreshold, index) => (
                  <ReferenceLine
                    key={`ref-line-${index}`}
                    label={{
                      position: "right",
                      value: additionalThresholdsActualValues[index],
                      width: 90,
                      fill: refLineColour,
                    }}
                    y={listThreshold}
                    stroke={refLineColour}
                    strokeDasharray="6 6"
                    strokeWidth="2"
                  />
                ))}
              {(!!threshold || threshold === 0) && (
                <ReferenceLine
                  label={
                    <CustomizedRefLabel
                      value={thresholdLabel}
                      fill={refLineColour}
                    />
                  }
                  y={threshold}
                  stroke={refLineColour}
                  strokeDasharray={
                    threshold && additionalThresholds ? "" : "6 6"
                  }
                  strokeWidth="2"
                />
              )}
              <Bar
                dataKey="beforeThreshold"
                stackId="a"
                maxBarSize={barSize}
                animationDuration={belowThresholdAnimationDuration}
              >
                {data.map((entry, index) => (
                  <Cell
                    id={`bar1-cell-${entry.name}-${index}`}
                    key={`bar1-cell-${entry.name}-${index}`}
                    fill={entry.underThresholdColour}
                  />
                ))}
              </Bar>
              <Bar
                dataKey="afterThreshold"
                stackId="a"
                animationDuration={belowThresholdAnimationDuration}
                animationBegin={belowThresholdAnimationDuration}
                maxBarSize={barSize}
                barCategoryGap={50}
                label={
                  !!moveOrigin && (
                    <CustomizedLabel
                      invertBarLabels={invertBarLabels}
                      mobileLayout={mobileLayout}
                      margins={margins}
                      barSize={barSize}
                      unit={unitOfMeasure}
                      success={success}
                      formatter={barLabelFormatterMobile}
                      colour={refLineColour}
                      isExample={isExample}
                    />
                  )
                }
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`bar2-cell-${entry.name}-${index}`}
                    fill={entry.overThresholdColour}
                  />
                ))}
                {!hideTopLabel && !isExample && (
                  <LabelList
                    formatter={barLabelFormatter}
                    content={stackedCustomizedLabel}
                    barSize={barSize}
                    screenWidth={width}
                  />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {!!needsLegend && (
            <div className={"flex flex-col pb-12 px-4"}>
              {data.map((item) => (
                <p key={`legend-${item.name}`} color="green !important">
                  {item.mobileName}: {item.name}
                </p>
              ))}
            </div>
          )}
        </>
      ) : (
        <ResponsiveContainer width="100%" height={150 * data.length + 100}>
          <BarChart
            data={data}
            barCategoryGap={2}
            barGap={1}
            margin={margins}
            layout="vertical"
          >
            <YAxis
              type="category"
              dataKey="name"
              tickLine={false}
              width={!!moveOrigin ? 0 : 100}
              tick={{ fill: "black", fontSize: "1.75rem !important" }}
              axisLine={!hideYAxisOnDesktop}
            />
            <XAxis
              type="number"
              domain={domain}
              tickCount={ticks?.length > 0 ? undefined : 6}
              tick={{ fill: hideNum ? "none" : "black", fontSize: "0.875rem" }}
              ticks={ticks?.length > 0 ? ticks : undefined}
              tickLine={showTicks}
            >
              <Label value={axisLabel} fill="black" position="bottom" />
            </XAxis>
            {!!moveOrigin && (
              <ReferenceLine
                key={`ref-line-origin`}
                label={{
                  position: "top",
                  value: originLabel,
                  width: 90,
                  fill: refLineColour,
                }}
                x={0}
                stroke={refLineColour}
                strokeWidth="3"
              />
            )}
            {additionalThresholds &&
              additionalThresholds.map((listThreshold, index) => (
                <ReferenceLine
                  key={`ref-line-${index}`}
                  label={{
                    position: "top",
                    value: additionalThresholdsActualValues[index],
                    width: 90,
                    fill: refLineColour,
                  }}
                  x={listThreshold}
                  stroke={refLineColour}
                  strokeDasharray="6 6"
                  strokeWidth="3"
                />
              ))}
            <ReferenceLine
              label={{
                position: "top",
                value: thresholdLabel,
                width: 180,
                fill: refLineColour,
              }}
              x={threshold}
              stroke={refLineColour}
              strokeDasharray={threshold && additionalThresholds ? "" : "6 6"}
              strokeWidth="3"
            />
            <Bar
              dataKey="beforeThreshold"
              stackId="a"
              maxBarSize={barSize}
              animationDuration={belowThresholdAnimationDuration}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.underThresholdColour} />
              ))}
            </Bar>
            <Bar
              dataKey="afterThreshold"
              stackId="a"
              maxBarSize={barSize}
              animationDuration={belowThresholdAnimationDuration}
              animationBegin={belowThresholdAnimationDuration}
              label={
                <CustomizedLabel
                  invertBarLabels={!!invertBarLabels}
                  overthreshold={true}
                  mobileLayout={mobileLayout}
                  margins={margins}
                  barSize={barSize}
                  unit={unitOfMeasure}
                  success={success}
                  formatter={barLabelFormatter}
                  colour={!moveOrigin ? refLineColour : undefined}
                  isExample={isExample}
                />
              }
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.overThresholdColour} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export const behaviourBarGraphControls = {
  data: {
    propType: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        color: PropTypes.string,
      }),
    ).isRequired,
    description: "Array of data points for the graph",
    controlType: "object",
    defaultValue: [
      { value: 50, label: "Jan", color: "blue" },
      { value: 75, label: "Feb", color: "green" },
    ],
  },
  threshold: {
    propType: PropTypes.number,
    description: "Primary threshold value for reference line",
    controlType: "number",
    defaultValue: 60,
  },
  additionalThresholdsRelative: {
    propType: PropTypes.arrayOf(PropTypes.number),
    description: "Additional threshold values (relative to data)",
    controlType: "object",
    defaultValue: [30, 90],
  },
  additionalThresholdsActualValues: {
    propType: PropTypes.arrayOf(PropTypes.number),
    description: "Additional threshold values (absolute values)",
    controlType: "object",
    defaultValue: [],
  },
  thresholdLabel: {
    propType: PropTypes.string,
    description: "Label for the primary threshold line",
    controlType: "text",
    defaultValue: "Target",
  },
  moveOrigin: {
    propType: PropTypes.bool,
    description: "Whether to move the origin point",
    controlType: "boolean",
    defaultValue: false,
  },
  originLabel: {
    propType: PropTypes.string,
    description: "Label for the origin point",
    controlType: "text",
    defaultValue: "Start",
  },
  domain: {
    propType: PropTypes.arrayOf(PropTypes.number),
    description: "Y-axis domain for desktop [min, max]",
    controlType: "object",
    defaultValue: [0, 100],
  },
  mobileDomain: {
    propType: PropTypes.arrayOf(PropTypes.number),
    description: "Y-axis domain for mobile [min, max]",
    controlType: "object",
    defaultValue: [0, 100],
  },
  refLineColour: {
    propType: PropTypes.string,
    description: "Color for reference lines",
    controlType: "color",
    defaultValue: "#888",
  },
  success: {
    propType: PropTypes.bool,
    description: "Success state styling",
    controlType: "boolean",
    defaultValue: false,
  },
  unitOfMeasure: {
    propType: PropTypes.string,
    description: "Unit of measurement for values",
    controlType: "text",
    defaultValue: "kg",
  },
  axisLabel: {
    propType: PropTypes.string,
    description: "Label for the Y-axis",
    controlType: "text",
    defaultValue: "Progress",
  },
  barLabelFormatter: {
    propType: PropTypes.func,
    description: "Function to format bar labels",
    disable: true,
    defaultValue: (value) => `${Math.floor(value)} kg`,
  },
  barLabelFormatterMobile: {
    propType: PropTypes.func,
    description: "Function to format mobile bar labels",
    disable: true,
  },
  invertBarLabels: {
    propType: PropTypes.bool,
    description: "Invert bar label colors for visibility",
    controlType: "boolean",
    defaultValue: false,
  },
  showTicks: {
    propType: PropTypes.bool,
    description: "Show Y-axis ticks",
    controlType: "boolean",
    defaultValue: true,
  },
  hideNum: {
    propType: PropTypes.bool,
    description: "Hide numerical values on bars",
    controlType: "boolean",
    defaultValue: false,
  },
  hideYLine: {
    propType: PropTypes.bool,
    description: "Hide Y-axis line",
    controlType: "boolean",
    defaultValue: false,
  },
  ticks: {
    propType: PropTypes.arrayOf(PropTypes.number),
    description: "Custom tick values for Y-axis",
    controlType: "object",
  },
  hideTopLabel: {
    propType: PropTypes.bool,
    description: "Hide top Y-axis label",
    controlType: "boolean",
    defaultValue: false,
  },
  hideYAxisOnDesktop: {
    propType: PropTypes.bool,
    description: "Hide Y-axis on desktop view",
    controlType: "boolean",
    defaultValue: false,
  },
  hideYLabelOnMobile: {
    propType: PropTypes.bool,
    description: "Hide Y-axis label on mobile",
    controlType: "boolean",
    defaultValue: false,
  },
  hideXLineOnMobile: {
    propType: PropTypes.bool,
    description: "Hide X-axis line on mobile",
    controlType: "boolean",
    defaultValue: false,
  },
};

BehaviourBarGraph.propTypes = AppUtils.getPropTypeFromControls(
  behaviourBarGraphControls,
);

export default BehaviourBarGraph;
